import { apis, isImpersonating } from '@monash/portal-frontend-common';

export const isValidJson = (value) => {
  try {
    JSON.parse(value);
  } catch (e) {
    return 'You must provide valid JSON';
  }
};

const SPECIAL_BLOCK_LIST = new Set([
  // Add APIs that should strictly NOT be called here
  'endImpersonation',
  'getMockUserIdentities',
  'getMockApis',
  'getApiDocs',
  'getIdentityAdmin',
]);

const SPECIAL_ALLOW_LIST = new Set([
  // Add non-get APIs that are allowed to be called here
  'search',
  'searchSuggestions',
]);

const isCallable = (apiName) => {
  return (
    (apiName.startsWith('get') || SPECIAL_ALLOW_LIST.has(apiName)) &&
    !SPECIAL_BLOCK_LIST.has(apiName)
  );
};

export const callableApis = Object.fromEntries(
  Object.entries(apis).filter(([apiName]) => isCallable(apiName))
);

const getAllApisAs = (initValue) => {
  return Object.fromEntries(
    Object.keys(callableApis).map((apiName) => [apiName, initValue])
  );
};

export const allApisTrue = getAllApisAs(true);
export const allApisFalse = getAllApisAs(false);

export const getResponses = async ({
  selectedApis,
  shouldFallbackForUnselected,
  unselectedFallback,
  errorFallback,
}) => {
  const allApiEntries = Object.entries(apis);
  const selectedApiEntries = allApiEntries.filter(
    ([apiName]) => selectedApis[apiName]
  );

  // if supplying fallbacks for unselected apis, then process all apis, else just process selected
  const requestedApiEntries = shouldFallbackForUnselected
    ? allApiEntries
    : selectedApiEntries;

  const requests = requestedApiEntries.map(([apiName, apiFunc]) => {
    try {
      const isSelected = selectedApis[apiName];
      return isSelected ? apiFunc() : unselectedFallback;
    } catch (error) {
      return errorFallback;
    }
  });

  const responses = await Promise.allSettled(requests);

  const output = Object.fromEntries(
    requestedApiEntries.map(([apiName], i) => {
      const response = responses[i];
      const responseValue =
        response.status === 'rejected' ? errorFallback : response.value;
      return [apiName, responseValue];
    })
  );

  return output;
};

// Transforming/redacting PII
export const getRedactedImpersonatee = async () => {
  const impersonateeData = await isImpersonating();

  // for max. safety, manually pick out the fields that should remain unchanged
  // instead of removing fields from the whole impersonateeData object
  const {
    currentlyImpersonating,
    embargo,
    hasEncumbrances,
    encumbranceEffectTypes,
    is,
    isBarred,
    organizationalRelationships,
  } = impersonateeData;

  if (!currentlyImpersonating) return null;

  const redactedImpersonatee = {
    embargo,
    hasEncumbrances,
    encumbranceEffectTypes,
    is,
    isBarred,
    organizationalRelationships,
  };

  Object.keys(redactedImpersonatee).forEach((key) => {
    if (!(key in impersonateeData)) delete redactedImpersonatee[key];
  });

  return redactedImpersonatee;
};

const getFakeEmail = (username) => {
  return `${username}@student.fake.edu`;
};

const API_RESPONSE_TRANSFORMS = {
  getIdentity: ({ fakeIdentity, redactedImpersonatee }) => {
    const { organizationalRelationships, is } = redactedImpersonatee;

    return {
      preferredName: fakeIdentity.preferredName,
      lastName: fakeIdentity.lastName,
      displayName: `${fakeIdentity.firstName} ${fakeIdentity.lastName}`,
      pronouns: fakeIdentity.pronouns,
      monashObjectID: fakeIdentity.uid,
      callistaPersonID: fakeIdentity.studentId,
      username: fakeIdentity.username,
      primaryEmail: getFakeEmail(fakeIdentity.username),
      organizationalRelationships,
      is,
    };
  },
  getProfileData: ({ fakeIdentity }) => {
    const legalName = `${fakeIdentity.firstName} ${fakeIdentity.lastName}`;

    const FAKE_ADDRESS = {
      addressLine1: '123 FAKE ST',
      addressLine2: null,
      addressLine3: null,
      addressLine4: null,
      attentionName: legalName,
      suburb: 'FAKE SUBURB',
      state: 'FAK',
      postCode: '0000',
    };

    return {
      legalName,
      firstName: fakeIdentity.firstName,
      lastName: fakeIdentity.lastName,
      preferredName: fakeIdentity.preferredName,
      pronouns: fakeIdentity.pronouns,
      id: fakeIdentity.studentId,
      email: getFakeEmail(fakeIdentity.username),
      postalAddress: {
        ...FAKE_ADDRESS,
        attentionName: legalName,
      },
      homeAddress: FAKE_ADDRESS,
      homePhoneNumber: null,
      mobilePhoneNumber: fakeIdentity.phoneNumber,
      cardholder: {
        cardNumber: fakeIdentity.studentId,
        cardIssueNumber: '01',
        generalBalance: 0,
        printingBalance: null,
      },
      emergencyContacts: [],
      disabilitySupportDetails: null,
      citizenshipType: fakeIdentity.citizenshipType,
      errors: [],
    };
  },
  getAvatar: ({ originalResponse, fakeIdentity }) => {
    const { portalAvatar } = originalResponse ?? {};

    return {
      mixAvatar: {
        type: 'image',
        id: fakeIdentity.studentId,
        image: null,
        lastModified: new Date(),
      },
      portalAvatar,
    };
  },
};

export const transformResponses = ({
  responses,
  redactedImpersonatee,
  fakeIdentity,
  selectedApis,
}) => {
  return Object.fromEntries(
    Object.entries(responses).map(([apiName, response]) => {
      const hasTransform = apiName in API_RESPONSE_TRANSFORMS;
      const isSelected = selectedApis[apiName];
      if (hasTransform && isSelected) {
        const transform = API_RESPONSE_TRANSFORMS[apiName];
        return [
          apiName,
          transform({
            originalResponse: response,
            redactedImpersonatee,
            fakeIdentity,
          }),
        ];
      }

      return [apiName, response];
    })
  );
};

export const getAnonymisedImpersonatee = ({
  fakeIdentity,
  redactedImpersonatee,
}) => {
  return {
    ...redactedImpersonatee,
    username: fakeIdentity.username,
    preferredName: fakeIdentity.preferredName,
    lastName: fakeIdentity.lastName,
    email: getFakeEmail(fakeIdentity.username),
    name: `${fakeIdentity.firstName} ${fakeIdentity.lastName}`,
    studentId: fakeIdentity.studentId,
    monashObjectId: fakeIdentity.uid,
    uid: fakeIdentity.uid,
    pronouns: fakeIdentity.pronouns,
  };
};
